/**
 * Module-singleton cache backing `useBridgeState`. Lives outside React entirely
 * so it survives `BridgeProvider` remounts.
 *
 * Responsibilities (REA-01):
 *  - Subscribe to a bridge namespace on first `subscribe` call; dedup subsequent
 *    subscribe calls via refcount so multiple hooks share one `state.subscribe`
 *  - Apply writes optimistically; send `state.update` to the sidecar; on
 *    rejection restore the previous value
 *  - Reconcile against `state.changed` broadcast echoes (`state.{namespace}` topic);
 *    no-op if echo matches optimistic, overwrite + notify otherwise
 *  - Handle `{ reset: true }` broadcasts by clearing the namespace's field map
 *  - Notify all listeners for a namespace whenever its state changes
 *
 * This file contains ZERO React imports. The React adapter is `useBridgeState.ts`.
 */
import type { BridgeClient } from './bridgeClient'
import type { StateChangedEvent } from './bridgeStateTypes'

interface PendingWrite {
  namespace: string
  path: string
  previous: unknown
}

function isSameJsonValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export interface BridgeStateCache {
  attachClient(client: BridgeClient): void
  subscribe(namespace: string, listener: () => void): () => void
  getSnapshot(namespace: string, path: string): unknown
  write(namespace: string, path: string, value: unknown): void
  reconcile(event: StateChangedEvent): void
  /** Test hook — clears all internal state. Do not call from app code. */
  _resetForTests(): void
}

export function createBridgeStateCache(): BridgeStateCache {
  let client: BridgeClient | null = null
  const state = new Map<string, Map<string, unknown>>()
  const refCounts = new Map<string, number>()
  const listeners = new Map<string, Set<() => void>>()
  const unsubscribers = new Map<string, () => void>()

  function notifyListeners(namespace: string): void {
    const set = listeners.get(namespace)
    if (!set) return
    set.forEach(fn => fn())
  }

  function ensureNamespaceMap(namespace: string): Map<string, unknown> {
    let map = state.get(namespace)
    if (!map) {
      map = new Map()
      state.set(namespace, map)
    }
    return map
  }

  function handleIncomingEvent(namespace: string, data: unknown): void {
    if (!data || typeof data !== 'object') return
    const payload = data as Record<string, unknown>
    console.warn('[BridgeStateCache] incoming event:', namespace, payload)

    // Reset event — clear the namespace
    if (payload.reset === true) {
      state.set(namespace, new Map())
      notifyListeners(namespace)
      return
    }

    // Change event — { path, value }
    if (typeof payload.path === 'string' && 'value' in payload) {
      const existing = state.get(namespace)?.get(payload.path)
      // Gotcha #7: skip notifying when echo matches optimistic value
      if (isSameJsonValue(existing, payload.value)) {
        return
      }
      ensureNamespaceMap(namespace).set(payload.path, payload.value)
      notifyListeners(namespace)
    }
  }

  return {
    attachClient(c: BridgeClient): void {
      client = c

      // When the bridge (re)connects, re-subscribe all active namespaces.
      // Without this, useBridgeState hooks that mount before the bridge is
      // ready silently fail their initial state.subscribe and never retry —
      // the namespace never receives broadcasts.
      const prevStatusChange = c.onStatusChange
      c.onStatusChange = (connected: boolean) => {
        prevStatusChange?.(connected)
        if (connected) {
          for (const ns of refCounts.keys()) {
            // Re-subscribe to event topic + send state.subscribe for snapshot
            const existingUnsub = unsubscribers.get(ns)
            existingUnsub?.()
            const unsub = c.subscribe(`state.${ns}`, (data: unknown) => {
              handleIncomingEvent(ns, data)
            })
            unsubscribers.set(ns, unsub)
            c.sendStateMessage('state.subscribe', { namespace: ns })
              .then((response: unknown) => {
                if (response && typeof response === 'object' && 'snapshot' in response) {
                  const snap = (response as { snapshot: unknown }).snapshot
                  if (snap && typeof snap === 'object') {
                    const map = ensureNamespaceMap(ns)
                    map.clear()
                    for (const [k, v] of Object.entries(snap as Record<string, unknown>)) {
                      map.set(k, v)
                    }
                    notifyListeners(ns)
                  }
                }
              })
              .catch(() => {})
          }
        }
      }
    },

    subscribe(namespace: string, listener: () => void): () => void {
      // 1) Register listener
      let set = listeners.get(namespace)
      if (!set) {
        set = new Set()
        listeners.set(namespace, set)
      }
      set.add(listener)

      // 2) Refcount dedup
      const current = refCounts.get(namespace) ?? 0
      refCounts.set(namespace, current + 1)

      // 3) First ref → real subscription
      if (current === 0 && client) {
        const c = client
        // Bridge event topic for this namespace
        const unsub = c.subscribe(`state.${namespace}`, (data: unknown) => {
          handleIncomingEvent(namespace, data)
        })
        unsubscribers.set(namespace, unsub)

        // Request initial snapshot — raw state protocol, not call_function
        c.sendStateMessage('state.subscribe', { namespace })
          .then((response: unknown) => {
            // Expected shape: { snapshot: { field: value, ... } }
            if (response && typeof response === 'object' && 'snapshot' in response) {
              const snap = (response as { snapshot: unknown }).snapshot
              if (snap && typeof snap === 'object') {
                const map = ensureNamespaceMap(namespace)
                map.clear()
                for (const [k, v] of Object.entries(snap as Record<string, unknown>)) {
                  map.set(k, v)
                }
                notifyListeners(namespace)
              }
            }
          })
          .catch(() => {
            // Best-effort: leave the namespace empty. Errors bubble up to
            // the toast layer in Phase 2 when the hook is wired into React.
          })
      }

      // 4) Return cleanup
      return () => {
        const s = listeners.get(namespace)
        s?.delete(listener)
        const count = refCounts.get(namespace) ?? 0
        const next = count - 1
        if (next > 0) {
          refCounts.set(namespace, next)
          return
        }
        refCounts.delete(namespace)
        listeners.delete(namespace)
        const unsub = unsubscribers.get(namespace)
        unsub?.()
        unsubscribers.delete(namespace)
        if (client) {
          client.sendStateMessage('state.unsubscribe', { namespace }).catch(() => {})
        }
      }
    },

    getSnapshot(namespace: string, path: string): unknown {
      return state.get(namespace)?.get(path)
    },

    write(namespace: string, path: string, value: unknown): void {
      if (!client) {
        console.warn('[BridgeStateCache] write SKIPPED (no client):', namespace, path, value)
        return
      }
      const map = ensureNamespaceMap(namespace)
      if (isSameJsonValue(map.get(path), value)) {
        return
      }
      console.warn('[BridgeStateCache] write:', namespace, path, value)
      const previous: PendingWrite = {
        namespace,
        path,
        previous: map.get(path),
      }
      map.set(path, value)
      notifyListeners(namespace)

      client.sendStateMessage('state.update', { namespace, path, value }).catch(() => {
        console.warn('[BridgeStateCache] write REJECTED, rolling back:', namespace, path)
        // Rollback
        const m = state.get(previous.namespace)
        if (m) {
          if (previous.previous === undefined) {
            m.delete(previous.path)
          } else {
            m.set(previous.path, previous.previous)
          }
          notifyListeners(previous.namespace)
        }
      })
    },

    reconcile(event: StateChangedEvent): void {
      handleIncomingEvent(event.namespace, { path: event.path, value: event.value })
    },

    _resetForTests(): void {
      client = null
      state.clear()
      refCounts.clear()
      listeners.clear()
      for (const u of unsubscribers.values()) u()
      unsubscribers.clear()
    },
  }
}

/** Module-singleton instance — `BridgeProvider` calls `attachClient` on mount. */
export const bridgeStateCache: BridgeStateCache = createBridgeStateCache()
